// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material'
import { DateRange, DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import { PoolTypeEnum, RoleEnum } from '@shared/Enums'
import { logOT } from '@shared/loggingShared'

import { EthAddr } from '@shared/SchemaTypes'
import dayjs, { Dayjs } from 'dayjs'
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import ReportExplorer from './ReportExplorer'

import { GridRowsProp } from '@mui/x-data-grid-premium'
import { fetchAllPoolAccountAccessPairs, fetchPoolParticipants } from '@react/fetchers/PoolFetchers'
import { poolActions } from '@react/redux/poolSlice'
import { PoolDynamicOverviewCTO, PoolDynamicOverviewCTOSchema } from '@shared/PoolDynamicCTO'
import { PoolFlexOverviewCTO } from '@shared/PoolFlexCTO'
import { PoolOverviewCTO } from '@shared/PoolTermCTO'
import minMax from 'dayjs/plugin/minMax'
import { ZeroAddress } from 'ethers'
import { useDispatch } from 'react-redux'
import { ReportDescriptor, ReportFetchParams, ReportSelectorRequirement, ReportSelectorType } from './ReportDescriptors'
export type RefreshHandle = {
  doRefresh: () => void
}

dayjs.extend(minMax)
export const ReportRowGenerator = forwardRef<
  RefreshHandle,
  {
    initialLoadWithoutRefresh: boolean
    reportDescriptor: ReportDescriptor
    numberOfDays?: number
    poolAddr?: EthAddr
    lenderAddr?: EthAddr
    dateRange?: [Dayjs, Dayjs]
    poolOverviewCTOs?: Record<EthAddr, PoolOverviewCTO | PoolFlexOverviewCTO | PoolDynamicOverviewCTO>
    accountPoolPairs?: Record<EthAddr, Record<EthAddr, boolean>>
  }
>((props, ref): React.ReactElement => {
  const { initialLoadWithoutRefresh, reportDescriptor, accountPoolPairs } = props
  const poolOverviewCTOsFromState = useOtSelector((state) => state.poolState.poolOverviewCTOs)
  const poolOverviewCTOs = props.poolOverviewCTOs ?? poolOverviewCTOsFromState
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | undefined>(undefined)

  const [lenderAddr, setLenderAddr] = useState<EthAddr | undefined>(props.lenderAddr)
  const [poolAddr, setPoolAddr] = useState<EthAddr | undefined>(props.poolAddr)
  const [lenderAddrs, setLenderAddrs] = useState<EthAddr[]>([])
  const [numberOfDays, setNumberOfDays] = useState<number | undefined>(
    props.numberOfDays ?? (reportDescriptor.selectors[ReportSelectorType.NUMBER_OF_DAYS] ? 7 : undefined)
  )
  const [loading, setLoading] = useState<boolean>(false)

  const [rows, setRows] = useState<GridRowsProp>([])
  const [fetchDataErrorMessage, setFetchDataErrorMessage] = useState<string | undefined>(undefined)
  const [currentFetchProgress, setCurrentFetchProgress] = useState<string | undefined>(undefined)
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    chainConfiguration: { chainTimestampDelta },
    userConnection: { accountAddr, roleEnum },
  } = useOtChainState()

  const dispatch = useDispatch()
  const theme = useTheme()

  useImperativeHandle(ref, () => ({
    doRefresh: () => {
      refreshData()
    },
  }))

  useEffect(() => {
    setDateRange
    setNumberOfDays(
      props.numberOfDays ?? (reportDescriptor.selectors[ReportSelectorType.NUMBER_OF_DAYS] ? 7 : undefined)
    )
    setRows([])
  }, [reportDescriptor])

  useEffect(() => {
    setLenderAddr(props.lenderAddr)
  }, [props.lenderAddr])

  useEffect(() => {
    if (reportDescriptor.selectors[ReportSelectorType.DATE_RANGE]) {
      const dateRangeDefaultString = localStorage.getItem('reportDateRange')
      if (dateRangeDefaultString) {
        const dateRangeDefault = JSON.parse(dateRangeDefaultString) as [Dayjs, Dayjs]
        localSetDateRange(dateRangeDefault)
      } else {
        // const utcNowAus = dayjs().tz('Australia/Sydney')
        // const utcStartOfDayAus = utcNowAus.startOf('day')

        const utcNowUTC = dayjs().utc()
        const utcStartOfDayUTC = utcNowUTC.startOf('day')
        // const utcEndOfDay = utcNow.endOf('day')
        localSetDateRange([utcStartOfDayUTC, utcStartOfDayUTC])
      }
    }
  }, [])
  useEffect(() => {
    const reportPoolAddr = localStorage.getItem('reportPoolAddr') as EthAddr | undefined
    if (
      reportDescriptor.selectors[ReportSelectorType.POOL_ADDR] &&
      reportPoolAddr &&
      setPoolAddr &&
      Object.keys(poolOverviewCTOs).includes(reportPoolAddr)
    ) {
      setPoolAddr(reportPoolAddr)
    }
  }, [poolOverviewCTOs])
  useEffect(() => {
    const reportLenderAddr = localStorage.getItem('reportLenderAddr') as EthAddr | undefined
    if (
      reportDescriptor.selectors[ReportSelectorType.LENDER_ADDR] &&
      reportLenderAddr &&
      setLenderAddr &&
      lenderAddrs &&
      lenderAddrs.includes(reportLenderAddr)
    ) {
      roleEnum === RoleEnum.LENDER ? setLenderAddr(accountAddr) : setLenderAddr(reportLenderAddr)
    }
  }, [lenderAddrs])

  useEffect(() => {
    if (poolAddr == ZeroAddress) {
      fetchAllPoolAccountAccessPairs(serverApiBaseUrl, restAPIKey).then((poolAccountPairs) => {
        logOT.info('fetchPoolAccountAccessPairRegeneration users', poolAccountPairs)
        const userSet = new Set<EthAddr>()
        for (const item of poolAccountPairs) {
          userSet.add(item.accountAddr)
        }
        const allUsers: EthAddr[] = Array.from(userSet)
        if (lenderAddr && !allUsers.includes(lenderAddr)) {
          setLenderAddr(undefined)
        }

        setLenderAddrs(allUsers)
      })
    } else if (poolAddr) {
      fetchPoolParticipants(serverApiBaseUrl, restAPIKey, poolAddr).then((pairs) => {
        const _lenderAddrs = pairs.map((pair) => pair.accountAddr)

        if (lenderAddr && !_lenderAddrs.includes(lenderAddr)) {
          setLenderAddr(undefined)
        }
        setLenderAddrs(_lenderAddrs)
        if (_lenderAddrs.length == 0) {
          fetchPoolParticipants(serverApiBaseUrl, restAPIKey, poolAddr).then((pairs) => {
            dispatch(poolActions.addPoolAccountAccessPairs({ poolAccountAccessPairs: pairs }))
          })
        }
      })
    }
  }, [poolAddr, accountPoolPairs])

  useEffect(() => {
    if (initialLoadWithoutRefresh) {
      refreshData()
    }
  }, [poolAddr, lenderAddr, dateRange, numberOfDays])

  const handlePoolAddrChange = (event: SelectChangeEvent) => {
    const poolAddr = event.target.value as EthAddr
    if (setPoolAddr) {
      setRows([])
      setPoolAddr(poolAddr)
      localStorage.setItem('reportPoolAddr', poolAddr)
    }
  }

  function dateRangeToDayStrings(dateRange?: [Dayjs, Dayjs]): { startDayStr?: string; endDayStr?: string } {
    const startDayStr = dateRange && dateRange.length > 1 ? dateRange[0].format('YYYY-MM-DD') : undefined
    const endDayStr = dateRange && dateRange.length > 1 ? dateRange[1].format('YYYY-MM-DD') : undefined
    return { startDayStr, endDayStr }
  }
  const localSetDateRange = (newDateRange: DateRange<Dayjs>) => {
    if (setDateRange && newDateRange[0] && newDateRange[1]) {
      logOT.info('localSetDateRange', dateRangeToDayStrings([newDateRange[0], newDateRange[1]]))
      setDateRange([newDateRange[0], newDateRange[1]])
    }
  }

  const refreshData = () => {
    setLoading(true)
    setRows([])
    const refreshAsyncFunction = async () => {
      const { startDayStr, endDayStr } = dateRangeToDayStrings(dateRange)

      const fetcherParams: ReportFetchParams = {
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        lenderAddr,
        startDayStr,
        endDayStr,
      }

      if (numberOfDays) {
        const endDate = dayjs().utc().add(chainTimestampDelta, 'seconds').startOf('day')
        const startDate = endDate.subtract(numberOfDays, 'days')
        fetcherParams.startDayStr = startDate.format('YYYY-MM-DD')
        fetcherParams.endDayStr = undefined
        const rowsForDays = await reportDescriptor.fetcher(fetcherParams)
        setRows(rowsForDays)
      } else if (dateRange && dateRange[0] && dateRange[1]) {
        const _rows = []
        // TODO: make this dynamic with a backoff on a timeout
        const DAYS_AT_A_TIME = 16
        let currentEndDay = dateRange[1]

        const d1 = currentEndDay.subtract(DAYS_AT_A_TIME - 1, 'days')
        let currentStartDay = dayjs.max(dateRange[0], d1)

        let cont = true
        while (cont) {
          const iterationFetchParams: ReportFetchParams = {
            ...fetcherParams,
            startDayStr: currentStartDay.format('YYYY-MM-DD'),
            endDayStr: currentEndDay.format('YYYY-MM-DD'),
          }

          const nextRows = await reportDescriptor.fetcher(iterationFetchParams)
          _rows.push(...nextRows)
          setRows(_rows)
          setCurrentFetchProgress(
            `Fetched ${currentStartDay.format('YYYY-MM-DD')} to ${dateRange[1].format('YYYY-MM-DD')}`
          )
          currentEndDay = currentStartDay.subtract(1, 'day')
          currentStartDay = dayjs.max(dateRange[0], currentEndDay.subtract(DAYS_AT_A_TIME - 1, 'days'))
          if (currentEndDay.isBefore(dateRange[0])) {
            cont = false
          }
        }
      } else {
        const _rows = await reportDescriptor.fetcher(fetcherParams)

        setRows(_rows)
      }
      // TODO
      setLoading(false)
    }
    refreshAsyncFunction()
      .then(() => {
        logOT.info('FINISHED FETCHING')
      })
      .catch((e) => {
        setFetchDataErrorMessage(e.message)
        logOT.error('Error refreshing data', e)
      })
  }

  const requiredParamsSatisfied =
    ((reportDescriptor.selectors[ReportSelectorType.POOL_ADDR] == ReportSelectorRequirement.REQUIRED ||
      reportDescriptor.selectors[ReportSelectorType.POOL_ADDR] == ReportSelectorRequirement.REQUIRED_OR_ALL) &&
      !poolAddr) ||
    (reportDescriptor.selectors[ReportSelectorType.LENDER_ADDR] == ReportSelectorRequirement.REQUIRED && !lenderAddr) ||
    (reportDescriptor.selectors[ReportSelectorType.DATE_RANGE] == ReportSelectorRequirement.REQUIRED && !dateRange) ||
    (reportDescriptor.selectors[ReportSelectorType.NUMBER_OF_DAYS] == ReportSelectorRequirement.REQUIRED &&
      !numberOfDays)
      ? false
      : true

  function getPoolOverviewsForSelector(): Array<PoolOverviewCTO | PoolFlexOverviewCTO | PoolDynamicOverviewCTO> {
    const poolOverviewsFiltered = Object.values(poolOverviewCTOs).filter(
      (poolOverviewCTO) =>
        reportDescriptor.poolTypeFilter == undefined || poolOverviewCTO.poolType === reportDescriptor.poolTypeFilter
    )

    if (reportDescriptor.selectors[ReportSelectorType.POOL_ADDR] == ReportSelectorRequirement.REQUIRED_OR_ALL) {
      const emptyPoolOverview: PoolDynamicOverviewCTO = PoolDynamicOverviewCTOSchema.cast({
        poolAddr: ZeroAddress,
        name: 'All Pools',
        poolType: PoolTypeEnum.NotPool,
      } as Partial<PoolDynamicOverviewCTO>)
      const poolOverviewsWithAllPools = [emptyPoolOverview, ...poolOverviewsFiltered]

      if (poolAddr && !poolOverviewsWithAllPools.find((poolOverview) => poolOverview.poolAddr === poolAddr)) {
        setRows([])
        setPoolAddr(undefined)
      }

      return poolOverviewsWithAllPools
    }
    if (poolAddr && !poolOverviewsFiltered.find((poolOverview) => poolOverview.poolAddr === poolAddr)) {
      setRows([])
      setPoolAddr(undefined)
    }

    return poolOverviewsFiltered
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          gap: '24px',
        }}
      >
        {reportDescriptor.selectors[ReportSelectorType.POOL_ADDR] && Object.keys(poolOverviewCTOs).length > 0 && (
          <FormControl sx={{ m: 1, width: 200 }} size="small">
            <InputLabel id="pool-label">Vault</InputLabel>
            <Select
              size="small"
              style={{ width: 200 }}
              labelId="pool-label"
              id="pool-select"
              input={<OutlinedInput id="pool-select" label="Pool" size="small" />}
              value={poolAddr ?? ('' as string)}
              renderValue={(value: string) => {
                return (
                  <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                    {value == ZeroAddress ? 'All Pools' : poolOverviewCTOs[value].name}
                  </Typography>
                )
              }}
              onChange={(event: SelectChangeEvent) => {
                handlePoolAddrChange(event)
              }}
            >
              {getPoolOverviewsForSelector().map((poolOverviewCTO) => {
                return (
                  <MenuItem
                    value={poolOverviewCTO.poolAddr}
                    key={poolOverviewCTO.poolAddr}
                    style={{
                      paddingLeft: 12,
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                    }}
                  >
                    <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                      {poolOverviewCTO.name}
                    </Typography>
                  </MenuItem>
                )
              })}
            </Select>

            {(reportDescriptor.selectors[ReportSelectorType.POOL_ADDR] == ReportSelectorRequirement.REQUIRED ||
              reportDescriptor.selectors[ReportSelectorType.POOL_ADDR] == ReportSelectorRequirement.REQUIRED_OR_ALL) &&
              !poolAddr && <FormHelperText error>* required</FormHelperText>}
          </FormControl>
        )}

        {reportDescriptor.selectors[ReportSelectorType.LENDER_ADDR] && roleEnum !== RoleEnum.LENDER && lenderAddrs && (
          <FormControl sx={{ m: 1, width: 200 }} size="small">
            <InputLabel id="lender-label">Lender</InputLabel>
            <Select
              size="small"
              disabled={!poolAddr}
              style={{ width: 200 }}
              labelId="lender-label"
              id="lender-select"
              input={<OutlinedInput id="lender-select" label="Lender" size="small" />}
              value={lenderAddr ?? ('' as string)}
              renderValue={(value: string) => {
                return (
                  <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                    {value}
                  </Typography>
                )
              }}
              onChange={(event: SelectChangeEvent) => {
                setLenderAddr(event.target.value as EthAddr)
                setRows([])
              }}
            >
              {lenderAddrs.map((value) => {
                return (
                  <MenuItem
                    value={value}
                    key={`${value}`}
                    style={{
                      paddingLeft: 12,
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                    }}
                  >
                    <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                      {value}
                    </Typography>
                  </MenuItem>
                )
              })}
            </Select>
            {!poolAddr &&
              (reportDescriptor.selectors[ReportSelectorType.POOL_ADDR] == ReportSelectorRequirement.REQUIRED ||
                reportDescriptor.selectors[ReportSelectorType.POOL_ADDR] ==
                  ReportSelectorRequirement.REQUIRED_OR_ALL) && (
                <FormHelperText error>Select Pool First</FormHelperText>
              )}
            {poolAddr &&
              reportDescriptor.selectors[ReportSelectorType.LENDER_ADDR] == ReportSelectorRequirement.REQUIRED &&
              !lenderAddr && <FormHelperText error>* required</FormHelperText>}
          </FormControl>
        )}

        {reportDescriptor.selectors[ReportSelectorType.DATE_RANGE] && (
          <Box sx={{ flexGrow: 0, width: 275 }}>
            {/* <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-au">  save for local testing of different locales*/}
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="UTC">
              <div className="flex flex-col">
                <DateRangePicker
                  minDate={
                    poolAddr
                      ? dayjs((poolOverviewCTOs[poolAddr]?.creationTimestamp ?? 0) * 1000)
                          .utc()
                          .add(1, 'day')
                          .startOf('day')
                      : undefined
                  }
                  maxDate={dayjs().utc().add(chainTimestampDelta, 'seconds').startOf('day')}
                  label={'Date Range'}
                  value={dateRange}
                  onChange={(newDateRange) => {
                    setRows([])
                    localSetDateRange(newDateRange)
                  }}
                />
              </div>
            </LocalizationProvider>
          </Box>
        )}
        {reportDescriptor.selectors[ReportSelectorType.NUMBER_OF_DAYS] && (
          <Box sx={{ flexGrow: 0, width: 275, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Date Range
            </Typography>
            <select
              value={numberOfDays}
              onChange={(e) => {
                setRows([])
                setNumberOfDays(Number(e.target.value))
              }}
              style={{ padding: '8px', borderRadius: '4px', width: '100%' }}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 365 days</option>
              <option value={0}>All</option>
            </select>
          </Box>
        )}

        {Object.keys(reportDescriptor.selectors).length > 0 && (
          <Box
            sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '24px' }}
          >
            <Button
              disabled={!requiredParamsSatisfied || loading}
              sx={{
                backgroundColor: theme.palette.fg.main,
                color: '#FFFFFF',
                padding: '8px 12px 8px 12px',
                borderRadius: '6px',
              }}
              onClick={() => {
                refreshData()
              }}
              variant={'contained'}
            >
              <Typography variant={'buttonBold'}>Fetch Data</Typography>
            </Button>
          </Box>
        )}
      </Box>
      {fetchDataErrorMessage && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            alignContent: 'center',
            margin: '8px',
          }}
        >
          <Typography textAlign={'center'} variant="bodyMuted">
            Error Fetching Data {fetchDataErrorMessage}
          </Typography>
        </Box>
      )}
      <ReportExplorer
        loading={loading}
        loadingProgress={currentFetchProgress}
        reportDescriptor={reportDescriptor}
        rows={rows}
      />
    </Box>
  )
})

ReportRowGenerator.displayName = 'ReportRowGenerator'
export default ReportRowGenerator
